"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NFID = void 0;
const tslib_1 = require("tslib");
const make_iframe_1 = require("./iframe/make-iframe");
const mount_iframe_1 = require("./iframe/mount-iframe");
const authentication_1 = require("./authentication");
const get_iframe_1 = require("./iframe/get-iframe");
const postmsg_rpc_1 = require("./postmsg-rpc");
class NFID {
    constructor(_nfidConfig) {
        this._nfidConfig = _nfidConfig;
        console.debug('NFID.constructor', { _nfidConfig });
    }
    static initIframe(nfidConfig) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            console.debug('NFID.initIframe', { nfidConfig });
            return new Promise((resolve) => {
                var _a, _b;
                const nfidIframe = (0, make_iframe_1.buildIframe)({
                    origin: nfidConfig.origin,
                    applicationName: (_a = nfidConfig.application) === null || _a === void 0 ? void 0 : _a.name,
                    applicationLogo: (_b = nfidConfig.application) === null || _b === void 0 ? void 0 : _b.logo,
                    onLoad: () => {
                        NFID.isIframeInstantiated = true;
                        NFID.nfidIframe = nfidIframe;
                        console.debug('NFID.initIframe: iframe loaded', { nfidIframe });
                        resolve(true);
                    },
                });
            });
        });
    }
    static init(params) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const { origin = 'https://nfid.one' } = params, nfidConfig = tslib_1.__rest(params, ["origin"]);
            console.debug('NFID.init', Object.assign({ origin }, nfidConfig));
            yield NFID.initIframe(Object.assign({ origin }, nfidConfig));
            console.debug('NFID.init iframe initiated');
            NFID._authClient = yield authentication_1.NfidAuthClient.create();
            console.debug('NFID.init authClient initiated');
            return new this(Object.assign({ origin }, nfidConfig));
        });
    }
    /**
     * Retrieves a delegation from the NFID iframe.
     * @param options An optional object containing the following properties:
     * @param options.targets An array of target strings.
     * @param options.targets:
     * @param options.maxTimeToLive: The maximum time to live as a BigInt.
     * @param options.derivationOrigin: The derivation origin as a string or URL.
     * @returns A promise that resolves with an Identity object or rejects with an error object.
     * @throws An error if the NFID iframe is not instantiated.
     */
    getDelegation(options) {
        var _a, _b;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            console.debug('NFID.connect');
            const derivationOrigin = (options === null || options === void 0 ? void 0 : options.derivationOrigin) || ((_b = (_a = this._nfidConfig) === null || _a === void 0 ? void 0 : _a.ic) === null || _b === void 0 ? void 0 : _b.derivationOrigin);
            if (!NFID.isIframeInstantiated)
                throw new Error('NFID iframe not instantiated');
            (0, mount_iframe_1.showIframe)();
            return new Promise((resolve, reject) => {
                NFID._authClient
                    .login(Object.assign(Object.assign({}, options), { derivationOrigin }))
                    .then((identity) => resolve(identity))
                    .catch((e) => reject({ error: e.message }))
                    .finally(mount_iframe_1.hideIframe);
            });
        });
    }
    /**
     * Updates the global delegation for the current user.
     * @param options - The options for the delegation update.
     * @param options.targets - The targets for the delegation update.
     * @param options.maxTimeToLive - The maximum time to live for the delegation update.
     * @returns A Promise that resolves with the delegation update response.
     * @throws Any error that occurs during the delegation update.
     */
    updateGlobalDelegation(options) {
        var _a, _b;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const derivationOrigin = (options === null || options === void 0 ? void 0 : options.derivationOrigin) || ((_b = (_a = this._nfidConfig) === null || _a === void 0 ? void 0 : _a.ic) === null || _b === void 0 ? void 0 : _b.derivationOrigin);
            console.debug('NFID.renewDelegation');
            const delegationType = NFID._authClient.getDelegationType();
            if (delegationType === authentication_1.DelegationType.ANONYMOUS)
                throw new Error('You can not update delegation from anonymous user');
            const response = yield NFID._authClient.renewDelegation(Object.assign(Object.assign({}, options), { derivationOrigin }));
            if ('error' in response)
                throw new Error(response.error.message);
            return response;
        });
    }
    getDelegationType() {
        return NFID._authClient.getDelegationType();
    }
    logout() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return NFID._authClient.logout();
        });
    }
    requestTransferFT({ receiver, amount, }) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            console.debug('NFID.requestTransferFT');
            const delegationType = NFID._authClient.getDelegationType();
            if (delegationType === authentication_1.DelegationType.ANONYMOUS)
                throw new Error('You can not call requestTransferFT from anonymous user');
            if (!NFID.nfidIframe)
                throw new Error('NFID iframe not instantiated');
            (0, mount_iframe_1.showIframe)();
            const iframe = (0, get_iframe_1.getIframe)();
            const response = yield (0, postmsg_rpc_1.request)(iframe, {
                method: 'ic_requestTransfer',
                params: [
                    {
                        receiver,
                        amount,
                    },
                ],
            });
            (0, mount_iframe_1.hideIframe)();
            if ('error' in response) {
                throw Error(response.error.message);
            }
            return response.result;
        });
    }
    requestTransferNFT({ receiver, tokenId, }) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            console.debug('NFID.requestTransferNFT');
            const delegationType = NFID._authClient.getDelegationType();
            if (delegationType === authentication_1.DelegationType.ANONYMOUS)
                throw new Error('You can not call requestTransferNFT from anonymous user');
            if (!NFID.nfidIframe)
                throw new Error('NFID iframe not instantiated');
            (0, mount_iframe_1.showIframe)();
            const iframe = (0, get_iframe_1.getIframe)();
            const response = yield (0, postmsg_rpc_1.request)(iframe, {
                method: 'ic_requestTransfer',
                params: [
                    {
                        receiver,
                        tokenId,
                    },
                ],
            });
            (0, mount_iframe_1.hideIframe)();
            if ('error' in response) {
                throw Error(response.error.message);
            }
            return response.result;
        });
    }
    /**
     * Sends a request to a 3rd party canister.
     * @param method - The method to call on the canister.
     * @param canisterId - The ID of the canister to call.
     * @param parameters - Optional parameters to pass to the canister method.
     * @param derivationOrigin - Optional derivation origin to use for the request.
     * @returns A Promise that resolves with the result of the canister call.
     */
    requestCanisterCall({ method, canisterId, parameters, derivationOrigin, }) {
        var _a, _b;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            console.debug('NFID.requestCanisterCall', {
                method,
                canisterId,
                parameters,
                derivationOrigin,
            });
            const delegationType = NFID._authClient.getDelegationType();
            if (delegationType === authentication_1.DelegationType.ANONYMOUS)
                throw new Error('You can not call requestCanisterCall from anonymous user');
            if (!NFID.nfidIframe)
                throw new Error('NFID iframe not instantiated');
            (0, mount_iframe_1.showIframe)();
            const iframe = (0, get_iframe_1.getIframe)();
            const response = yield (0, postmsg_rpc_1.request)(iframe, {
                method: 'ic_canisterCall',
                params: [
                    {
                        method,
                        canisterId,
                        parameters,
                        derivationOrigin: derivationOrigin || ((_b = (_a = this._nfidConfig) === null || _a === void 0 ? void 0 : _a.ic) === null || _b === void 0 ? void 0 : _b.derivationOrigin),
                    },
                ],
            });
            (0, mount_iframe_1.hideIframe)();
            if ('error' in response) {
                throw Error(response.error.message);
            }
            return response.result;
        });
    }
    get isAuthenticated() {
        return NFID._authClient.isAuthenticated;
    }
    getIdentity() {
        return NFID._authClient.getIdentity();
    }
}
NFID.isIframeInstantiated = false;
exports.NFID = NFID;
